import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Camera } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import type {
  CanvasCapture,
  CanvasSession,
  Persona,
  ResearchSessionStatus,
} from '@repo/contracts';

import { trackEvent } from '../analytics/posthog';
import { PersonaAvatar } from '../persona/persona-avatar';
import { progressLine, sessionNext } from './session-stage';
import { StagePill } from './stage-pill';

dayjs.extend(relativeTime);

/** How many panel faces a card shows before it just counts. */
const FACES = 4;

/** "Just now" under a minute, then dayjs's own "5 minutes ago". */
export function whenLine(iso: string, now = dayjs()): string {
  return now.diff(dayjs(iso), 'second') < 60
    ? 'Just now'
    : dayjs(iso).from(now);
}

/**
 * One room, as a card: its screen on the room's pale violet, its name, how
 * far its research has got, and who is on its panel.
 *
 * The whole card opens the room. A report is the one thing worth reaching
 * without going in, so once there is one, a pill on the screen links to it.
 */
export function SessionCard({
  capture,
  index,
  panel,
  session,
  status,
}: {
  /** The room's first screen, for the thumbnail. */
  capture?: CanvasCapture;
  index: number;
  /** Its site's panel — the people a room starts with. */
  panel: Persona[];
  session: CanvasSession;
  status?: ResearchSessionStatus;
}) {
  const next = sessionNext(session.id, status);
  const [loaded, setLoaded] = useState(false);
  const title = session.name ?? session.label;
  const reported =
    next.stage === 'ready' ||
    next.stage === 'writing' ||
    next.stage === 'failed';

  return (
    <li
      className="duration-500 animate-in fade-in slide-in-from-bottom-2 fill-mode-both motion-reduce:animate-none"
      style={{ animationDelay: `${String(Math.min(index, 8) * 50)}ms` }}
    >
      <article className="group relative flex h-full flex-col overflow-hidden rounded-room bg-surface p-2.5 shadow-room transition duration-200 hover:-translate-y-0.5 motion-reduce:hover:translate-y-0">
        <Link
          aria-label={`Open ${title}`}
          className="absolute inset-0 z-10 rounded-room focus-visible:ring-2 focus-visible:ring-ink focus-visible:outline-none"
          onClick={() => {
            trackEvent('room_card_opened', {
              captures: session.captureCount,
              position: index,
              stage: next.stage,
              target: 'card',
            });
          }}
          state={{ from: 'card' }}
          to={`/sessions/${session.id}`}
        />
        <div className="relative aspect-[350/176] overflow-hidden rounded-button bg-violet-soft">
          {capture ? (
            <div className="absolute inset-x-[8.6%] top-[16%] bottom-0 overflow-hidden rounded-t-button shadow-soft">
              <div
                className={`absolute inset-0 bg-warm-alt transition-opacity duration-500 ${loaded ? 'opacity-0' : 'animate-pulse'}`}
              />
              <img
                alt=""
                className={`absolute inset-0 size-full object-cover object-top transition duration-700 group-hover:scale-[1.02] ${loaded ? 'opacity-100' : 'opacity-0'}`}
                decoding="async"
                onLoad={() => {
                  setLoaded(true);
                }}
                src={capture.imageUrl}
              />
            </div>
          ) : (
            <div className="flex size-full flex-col items-center justify-center gap-2 text-text-muted">
              <Camera aria-hidden="true" className="size-5" />
              <span className="text-xs">No screens yet</span>
            </div>
          )}
          {reported ? (
            // Above the card's own link, so a ready report opens directly.
            <Link
              className="absolute top-2.5 left-2.5 z-20"
              onClick={() => {
                trackEvent('room_card_opened', {
                  captures: session.captureCount,
                  position: index,
                  stage: next.stage,
                  target: 'action',
                });
              }}
              state={{ from: 'card' }}
              to={next.href}
            >
              <StagePill label={next.label} stage={next.stage} />
            </Link>
          ) : null}
        </div>

        <div className="flex flex-1 flex-col px-2.5 pt-4 pb-1.5">
          <p className="truncate font-heading text-room-title text-text-primary">
            {title}
          </p>
          <p className="mt-1 truncate text-ui text-text-secondary">
            {/* No host for a room started from a screenshot alone. */}
            {session.host ? `${session.host} · ` : ''}
            {progressLine(status)}
          </p>

          <div className="mt-auto flex items-center justify-between gap-3 pt-3">
            <div className="flex min-w-0 items-center gap-2">
              {panel.length > 0 ? (
                <>
                  <span aria-hidden="true" className="flex shrink-0">
                    {panel.slice(0, FACES).map((persona, face) => (
                      <PersonaAvatar
                        className={face === 0 ? '' : '-ml-[7px]'}
                        key={persona.id}
                        persona={persona}
                        size="sm"
                      />
                    ))}
                  </span>
                  <span className="truncate text-tag-lg font-semibold text-text-secondary">
                    {panel.length} persona{panel.length === 1 ? '' : 's'}
                  </span>
                </>
              ) : null}
            </div>
            <span className="flex h-6 shrink-0 items-center rounded-pill bg-warm px-2.5 text-tag-lg font-semibold text-text-secondary">
              {whenLine(status?.lastActivityAt ?? session.startedAt)}
            </span>
          </div>
        </div>
      </article>
    </li>
  );
}
